/** biome-ignore-all lint/a11y/noNoninteractiveElementToInteractiveRole: avoid-breaking */

import React, { type FC } from 'react';

import { Collapse } from '../../Collapse/Collapse';
import { Typography } from '../../Typography/Typography';
import { DEFAULT_LOCALE } from '../Constants/Header.constants';
import { useNavigationItem } from '../Hooks/UseNavigationItem';
import type { NavigationProps } from './Navigation.types';
import SubNav from './SubNav';

import styles from '../Styles/NavigationDropdown.module.css';

const NavigationDropdown: FC<NavigationProps> = React.forwardRef<HTMLDivElement, NavigationProps>(
  (
    {
      activeNavItem,
      currentRoute,
      dataTestId,
      itemOnClick,
      Link,
      locale = DEFAULT_LOCALE,
      navigation = [],
      setActiveNavItem,
      trackingFn,
    },
    ref,
  ) => {
    const { toggleSubNav, keyboardOpen, closeSiblings } = useNavigationItem(
      activeNavItem,
      setActiveNavItem,
    );

    return (
      <nav
        className={styles.nav}
        data-nav-type="dropdown-menu"
        data-test-id={dataTestId}
        ref={ref}
        role="navigation"
      >
        <ul
          aria-label="Main navigation"
          role="menubar"
          tabIndex={0}
          onMouseLeave={() => {
            setActiveNavItem(undefined);
          }}
        >
          {navigation.map((navItem, i) => {
            const { label, subNavGroups } = navItem;
            const id = `nav-item-${label.replace(/^[^a-z]+|[^\w:.-]+/gi, '')}`;
            const hasSubNav = subNavGroups && subNavGroups?.length > 0;
            const isActive = activeNavItem === id;
            const isCurrentPage = currentRoute && navItem.href && currentRoute === navItem.href;
            const isLastItem = i + 1 === navigation.length;
            const commonProps = {
              role: 'menuitem',
              className: `${styles.navItem} ${isActive ? styles.isActive : ''} ${
                isCurrentPage ? styles.isCurrentPage : ''
              }`,
              id,
            };

            return (
              <li
                className={`${isLastItem ? styles.lastNavItem : ''} ${styles.parentNavItem}`}
                key={`dropdown-${label}`}
                data-is-active={isActive}
                onMouseEnter={() => {
                  toggleSubNav(id, isActive, true);
                }}
                role="none"
              >
                {hasSubNav ? (
                  <>
                    <button
                      {...commonProps}
                      aria-expanded={isActive}
                      aria-haspopup={true}
                      aria-label={`${label} - ${locale.spaceBarNotification}`}
                      onClick={() =>
                        itemOnClick(
                          {
                            fn: trackingFn,
                            obj: {
                              label: `${label}`,
                              type: 'nav_item',
                              navType: 'dropdown',
                            },
                          },
                          () => toggleSubNav(id, isActive),
                        )
                      }
                      onFocus={() => closeSiblings(id, hasSubNav)}
                      onKeyDown={e => keyboardOpen(e, id, isActive)}
                    >
                      <Typography variant="button2">{label}</Typography>
                    </button>
                    <Collapse
                      aria-hidden={!isActive}
                      aria-label={`${label} ${locale.subMenuLabel}`}
                      className={styles.collapse}
                      open={true}
                      role="group"
                      showDropShadow
                      onMouseOver={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseOut={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <SubNav
                        Link={Link}
                        isDropDownMenu={true}
                        isOpen={isActive}
                        itemOnClick={itemOnClick}
                        locale={locale}
                        navItemId={commonProps.id}
                        navItemName={label}
                        subNavGroups={subNavGroups!}
                        trackingFn={trackingFn}
                      />
                    </Collapse>
                  </>
                ) : (
                  <Link
                    {...commonProps}
                    href={navItem.href}
                    onClick={() =>
                      itemOnClick(
                        {
                          fn: trackingFn,
                          obj: {
                            label: `${label}`,
                            type: 'nav_item',
                            href: navItem.href,
                            navType: 'dropdown',
                          },
                        },
                        navItem.onClick,
                      )
                    }
                  >
                    <Typography variant="button2">{label}</Typography>
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    );
  },
);

export default NavigationDropdown;
